import { Component, Input, OnInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { ApiService } from 'src/app/services/api/api.service';
import { SignupService } from 'src/app/services/signup/signup.service';
import { ToastService } from 'src/app/services/toast/toast.service';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, Validators } from '@angular/forms';
import { AuthService } from 'src/app/services/auth/auth.service';
import { CompanyService } from 'src/app/services/company/company.service';
import { CompanyApiUtils } from 'src/app/helpers/utils';
import { Subject } from 'rxjs';
import Utils from 'src/app/helpers/utils';

@Component({
  selector: 'app-dashboard-companies-details-departments',
  templateUrl: './dashboard-companies-details-departments.component.html',
  styleUrls: ['./dashboard-companies-details-departments.component.scss'],
})
export class DashboardCompaniesDetailsDepartmentsComponent implements OnInit, OnDestroy, OnChanges {
  private destroy$ = new Subject<void>();
  private loadingInProgress = false;
  @Input() companyId: number = 0;
  @Input() activeTab: string = '';
  permissionObj = {
    canAdd: true,
    canFilter: true,
    canChange: true,
    canInactive: true,
  };

  data: CompanyDepartmentValues[] = [];

  filterForm = this.formBuilder.group({
    department: [null, []],
    start_date: [
      null,
      [
        Utils.validateWhenNotEmpty(Utils.validateDateValid),
        Utils.validateWhenNotEmpty(Utils.validateMaxMinToday),
      ],
    ],
    end_date: [
      null,
      [
        Utils.validateWhenNotEmpty(Utils.validateDateValid),
        Utils.validateWhenNotEmpty(Utils.validateMaxMinToday),
      ],
    ],
    profiles: [null, []],
  });

  departmentForm = this.formBuilder.group({
    department: [null, [Validators.required]],
    roles: [null, [Validators.required]],
  });

  newDepartment = false;
  loading = true;
  /**
   * Padrão de Filtros:
   * - hasFilters só deve ser true APÓS aplicar os filtros (método filterData())
   * - NÃO atualizar hasFilters no valueChanges do filterForm
   * - hasFilters deve ser atualizado apenas quando:
   *   1. filterData() executar com sucesso (após a requisição)
   *   2. resetForm() for chamado (define como false)
   *   3. toggleFilterModal() quando há filtros salvos no storage
   */
  hasFilters = false;
  appliedFilters: Array<{ label: string; value: string }> = [];
  showFilterModal = false;
  offset: number = 0;
  count: number = 0;
  profileOptions: SelectItem[] = [];
  editingId;

  // Propriedades para o warning modal
  showStatusModal = false;
  selectedDepartment: any = null;
  isActive: boolean = false;
  addingDepartment: boolean = false;

  constructor(
    private formBuilder: FormBuilder,
    private api: ApiService,
    private toast: ToastService,
    public signupService: SignupService,
    private route: ActivatedRoute,
    private authService: AuthService,
    private companyService: CompanyService
  ) {}

  private dataLoaded = false;

  // Helper para verificar se está na rota /app/company (sem companyId)
  get isCompanyRoute(): boolean {
    return !this.companyId || this.companyId === 0;
  }

  ngOnInit(): void {
    this.setPermissions();
    // Lazy loading: só carrega dados quando componente é inicializado (quando aba está ativa)
    if (!this.dataLoaded) {
      this.dataLoaded = true;
      this.getData();
      // Só busca perfis se não estiver na rota /app/company
      if (!this.isCompanyRoute) {
        this.getProfiles();
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.loadingInProgress = false;
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Quando a aba mudar e não for mais 'departments', resetar os filtros
    if (
      changes['activeTab'] &&
      changes['activeTab'].previousValue === 'departments' &&
      changes['activeTab'].currentValue !== 'departments'
    ) {
      this.resetFiltersOnTabChange();
    }
    // Quando voltar para a aba 'departments', recarregar dados sem filtros
    else if (changes['activeTab'] && changes['activeTab'].currentValue === 'departments') {
      // Verifica se o componente já foi inicializado
      if (this.dataLoaded) {
        // Resetar filtros e recarregar dados
        this.hasFilters = false;
        this.appliedFilters = [];
        this.filterForm.reset();
        this.getData();
      }
    }
  }

  resetFiltersOnTabChange() {
    this.hasFilters = false;
    this.appliedFilters = [];
    this.filterForm.reset();
  }

  async getProfiles(page: number = 1) {
    // Não busca perfis na rota /app/company (sem companyId)
    if (this.isCompanyRoute) {
      this.profileOptions = [];
      return;
    }

    try {
      const id = this.route.snapshot.paramMap.get('id');

      const res = await this.api.get({
        route: `api/v3/companies/${id}/`,
        params: {
          tab: 'user_profile',
        },
        token: true,
      });

      // V3 sempre retorna paginação
      const profiles = res.results || res;
      this.profileOptions = profiles.map((item) => ({
        label: item.description,
        value: item.id,
        extra: item.role_id,
      }));
    } catch (error) {
      console.error(error);
      this.toast.show('error', 'Erro', 'Ocorreu um erro ao carregar os dados.');
    }
    this.loading = false;
  }

  async getData(page: number = 1) {
    // Evitar múltiplas requisições simultâneas
    if (this.loadingInProgress) {
      return;
    }
    this.loadingInProgress = true;
    this.loading = true;
    try {
      // Se companyId for 0, undefined ou null, busca da API department/ (rota /app/company)
      if (this.isCompanyRoute) {
        const response: any = await this.api.get({
          route: 'department/',
          token: true,
        });

        // Transforma os dados da API department/ para o formato esperado
        this.data = (response || []).map((item: any) => ({
          id: item.id,
          department: item.name,
          users_type: [],
          updated_at: null,
          status: 'active',
        }));

        this.offset = this.data.length;
        this.count = this.data.length;
      } else {
        // Comportamento original: busca departamentos da empresa
        const id = this.route.snapshot.paramMap.get('id');
        const filters: any = {};

        // Construir filtros a partir do formulário (se houver valores)
        const values = this.filterForm.value;
        Object.entries(values).forEach(([key, value]: any) => {
          if (value !== null && value !== '') {
            // Verificar se o campo tem erro de validação antes de incluir
            const control = this.filterForm.get(key);
            if (control && control.valid) {
              let newValue = value;
              if (key === 'start_date' || key === 'end_date') {
                newValue = value.split('/').reverse().join('-');
              }
              filters[key] = newValue;
            }
          }
        });

        if (filters['start_date'] && !filters['end_date']) {
          const atualDate = new Date().toISOString().split('T')[0];
          filters['end_date'] = atualDate;
        }

        if (filters['end_date'] && !filters['start_date']) {
          filters['start_date'] = '2000-01-01';
        }

        let params: any = {
          tab: 'departments',
          page: page,
          ...filters,
        };

        const response: any = await this.api.get({
          route: `api/v3/companies/${id}/`,
          token: true,
          params,
        });

        // V3 sempre retorna paginação
        this.offset = response.showing_to || 0;
        this.count = response.count || 0;
        // Atribuir dados direto sem processamento (como outras páginas que funcionam)
        // O processamento será feito sob demanda no template através dos métodos displayDateTime() e getStatusDisplay()
        this.data = response.results || [];
      }
    } catch (error) {
      console.error(error);
      this.toast.show('error', 'Erro', 'Ocorreu um erro ao carregar os dados.');
    } finally {
      this.loading = false;
      this.loadingInProgress = false;
    }
  }

  handleCancelDepartment() {
    this.newDepartment = false;
    this.editingId = null;
    this.departmentForm.reset();
  }

  changePageData(page: number) {
    this.loading = true;
    this.getData(page);
  }

  resetForm() {
    this.loading = true;
    this.hasFilters = false;
    this.appliedFilters = [];
    this.filterForm.reset();
    this.getData();
  }

  generateFilterTags(formValues: any) {
    this.appliedFilters = [];

    const fieldLabels: { [key: string]: string } = {
      department: 'Departamento',
      profiles: 'Perfis de acessos',
      start_date: 'Data inicial',
      end_date: 'Data final',
    };

    Object.entries(formValues).forEach(([key, value]: any) => {
      if (value !== null && value !== '' && value !== undefined) {
        let displayValue = value;

        // Tratamento especial para arrays (múltipla seleção)
        if (Array.isArray(value) && value.length > 0) {
          if (key === 'profiles') {
            displayValue = value
              .map((id: number) => {
                const profile = this.profileOptions.find((p) => p.value === id);
                return profile ? profile.label : id;
              })
              .join(', ');
          } else {
            displayValue = value.join(', ');
          }
        }

        // Tratamento especial para datas (já vem formatado do formulário)
        if (key === 'start_date' || key === 'end_date') {
          displayValue = value;
        }

        if (fieldLabels[key]) {
          // Combinar label e valor no formato "label: valor"
          const combinedLabel = `${fieldLabels[key]}: ${String(displayValue)}`;
          this.appliedFilters.push({
            label: combinedLabel,
            value: String(displayValue),
          });
        }
      }
    });
  }

  toggleFilterModal() {
    this.showFilterModal = !this.showFilterModal;

    if (this.showFilterModal) {
      // Se houver filtros aplicados, restaurar no formulário
      if (this.hasFilters && this.appliedFilters.length > 0) {
        // Os valores já estão no formulário, apenas garantir que as tags estão atualizadas
        this.generateFilterTags(this.filterForm.value);
      } else {
        this.hasFilters = false;
        this.appliedFilters = [];
      }
    }
  }

  async filterData() {
    // Não permite filtrar na rota /app/company (sem companyId)
    if (this.isCompanyRoute) {
      this.toast.show('info', 'Atenção', 'Filtros não disponíveis nesta visualização.');
      return;
    }

    // Verificar se o formulário é válido antes de processar
    if (this.filterForm.invalid) {
      // Marcar todos os campos como touched para mostrar erros
      Object.keys(this.filterForm.controls).forEach((key) => {
        this.filterForm.get(key)?.markAsTouched();
      });
      return;
    }

    this.loading = true;

    try {
      const values = this.filterForm.value;
      const id = this.route.snapshot.paramMap.get('id');

      const filters: any = {};

      Object.entries(values).forEach(([key, value]: any) => {
        if (value !== null && value !== '') {
          // Verificar se o campo tem erro de validação antes de incluir
          const control = this.filterForm.get(key);
          if (control && control.valid) {
            let newValue = value;
            if (key === 'start_date' || key === 'end_date') {
              newValue = value.split('/').reverse().join('-');
            }
            filters[key] = newValue;
          }
        }
      });

      if (filters['start_date'] && !filters['end_date']) {
        const atualDate = new Date().toISOString().split('T')[0];
        filters['end_date'] = atualDate;
      }

      if (filters['end_date'] && !filters['start_date']) {
        filters['start_date'] = '2000-01-01';
      }

      let params: any = {
        tab: 'departments',
        page: 1,
        ...filters,
      };

      const response: any = await this.api.get({
        route: `api/v3/companies/${id}/`,
        token: true,
        params,
      });

      // ✅ IMPORTANTE: hasFilters só é atualizado após aplicar os filtros com sucesso
      this.hasFilters = Object.keys(filters).length > 0;

      // ✅ Gerar as tags dos filtros aplicados
      this.generateFilterTags(values);

      // V3 sempre retorna paginação
      this.count = response.count || 0;
      this.offset = response.showing_to || 0;
      // Atribuir dados direto sem processamento (como outras páginas que funcionam)
      // O processamento será feito sob demanda no template através dos métodos displayDateTime() e getStatusDisplay()
      this.data = response.results || [];
      this.showFilterModal = false;
    } catch (error) {
      console.error(error);
      this.toast.show('error', 'Erro', 'Ocorreu um erro ao carregar os dados.');
    } finally {
      this.loading = false;
      this.loadingInProgress = false;
    }
  }

  setPermissions() {
    const permissions = Object.keys(this.permissionObj).map(
      (key) =>
        `de_can_${key.split('can').join('').toLowerCase()}_company_departments`
    );

    permissions.forEach((permission, idx) => {
      this.permissionObj[Object.keys(this.permissionObj)[idx]] =
        this.authService.verifyPermission(permission);
    });
  }

  async handleAddDepartment() {
    // Não permite adicionar departamento na rota /app/company (sem companyId)
    if (this.isCompanyRoute) {
      this.toast.show('info', 'Atenção', 'Não é possível adicionar departamentos nesta visualização.');
      return;
    }

    this.addingDepartment = true;
    try {
      const values = this.departmentForm.value;
      const profilesID = values.roles;

      const roles = values.roles.map((item) => {
        const finded = this.profileOptions.find(
          (profile) => profile.value === item
        );
        return finded ? finded.extra : null;
      });

      const obj = {
        ...values,
        roles,
        company: this.companyId,
        active: true,
        profiles: profilesID,
      };

      const res = this.editingId
        ? await this.api.patch({
            route: `api/v2/company_department/${this.editingId}/`,
            token: true,
            body: obj,
          })
        : await this.api.post({
            route: 'api/v2/company_department/',
            token: true,
            body: obj,
          });

      this.editingId = null;
      this.newDepartment = false;
      this.departmentForm.reset();
      this.loading = true;
      this.getData();
      this.companyService.emitDepartmentAdded();
      this.toast.show(
        'info',
        'Sucesso',
        'Departamento adicionado com sucesso.'
      );
    } catch (error) {
      console.error('Erro ao adicionar departamento:', error);
      this.toast.show(
        'error',
        'Erro',
        'Ocorreu um erro ao adicionar o departamento.'
      );
    } finally {
      this.addingDepartment = false;
    }
  }

  handleEdit(id: number) {
    // Não permite editar departamento na rota /app/company (sem companyId)
    if (this.isCompanyRoute) {
      this.toast.show('info', 'Atenção', 'Não é possível editar departamentos nesta visualização.');
      return;
    }

    this.newDepartment = true;
    const finded = this.data.find((item) => item.id === id);
    this.editingId = id;

    const extraIds = finded.users_type.map((item) => item.id);

    const ids = this.profileOptions
      .filter((item) => extraIds.includes(item.value))
      .map((item) => item.value);

    this.departmentForm.patchValue({
      department: finded.department,
      roles: ids,
    });
  }

  handleClose() {
    this.newDepartment = false;
    this.editingId = null;
    this.departmentForm.reset();
  }

  renderUsersType(users_type: any[]) {
    if (!users_type || users_type.length === 0) {
      return '-';
    }

    const labels = users_type.map((item) => item.description);

    const displayLabels =
      labels.length > 3
        ? [...labels.slice(0, 3), `+ ${labels.length - 3} itens`]
        : labels;

    return displayLabels.join(', ');
  }

  toggleStatus(id: number) {
    const finded: any = this.data.find((company) => company.id === id);
    this.selectedDepartment = finded;
    // V3 usa 'active'/'inactive'
    this.isActive = finded?.status === 'active';
    this.showStatusModal = true;
  }

  async confirmStatusChange() {
    // Não permite alterar status na rota /app/company (sem companyId)
    if (this.isCompanyRoute) {
      this.toast.show('info', 'Atenção', 'Não é possível alterar o status do departamento nesta visualização.');
      this.closeStatusModal();
      return;
    }

    try {
      const selectedDept: any = this.selectedDepartment;
      const res = await this.api.patch({
        route: `api/v2/company_department/${selectedDept.id}/`,
        token: true,
        body: {
          company: this.companyId,
          // V3 usa 'active'/'inactive', mas a API ainda pode esperar 'active' boolean
          active: selectedDept.status === 'active' ? false : true,
        },
      });

      this.loading = true;
      this.showStatusModal = false;
      this.getData();
    } catch (error) {
      console.error(error);
      this.toast.show(
        'error',
        'Erro',
        'Ocorreu um erro ao alterar o status do departamento.'
      );
    }
  }

  closeStatusModal() {
    this.showStatusModal = false;
    this.selectedDepartment = null;
    this.isActive = false;
  }

  getModalTitle(): string {
    return this.isActive ? 'Inativar departamento' : 'Ativar departamento';
  }

  getModalMessage(): string {
    return `Deseja ${
      this.isActive ? 'inativar' : 'ativar'
    } esse departamento? Todas as permissões vinculadas a esse departamento também serão afetadas.`;
  }

  getConfirmText(): string {
    return `Sim, ${this.isActive ? 'inativar' : 'ativar'} departamento`;
  }

  async refreshData() {
    await this.getData();
  }

  getStatusDisplay(status: string): string {
    return CompanyApiUtils.getStatusDisplay(status, 'general');
  }

  displayDateTime(dateStr: string) {
    if (!dateStr) return { date: '-', time: '' };

    try {
      // Se já vem no formato "DD/MM/YYYY HH:MM"
      if (dateStr.includes('/') && dateStr.includes(' ')) {
        const [datePart, timePart] = dateStr.split(' ');
        const [day, month, year] = datePart.split('/');
        const [hours, minutes] = timePart.split(':');
        
        return {
          date: `${day}/${month}/${year}`,
          time: `às ${hours}:${minutes}`,
        };
      }
      
      // Se for ISO datetime
      const date = new Date(dateStr);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');

      return {
        date: `${day}/${month}/${year}`,
        time: `às ${hours}:${minutes}`,
      };
    } catch (error) {
      return { date: dateStr || '-', time: '' };
    }
  }

  trackByItemId(index: number, item: any): any {
    return item?.id || index;
  }

  /**
   * Verifica se os filtros podem ser aplicados
   * Retorna false se houver campos inválidos ou se não houver nenhum filtro preenchido
   */
  get canApplyFilters(): boolean {
    // Verifica se o formulário é válido
    if (this.filterForm.invalid) {
      return false;
    }

    // Verifica se há pelo menos um filtro preenchido
    const values = this.filterForm.value;
    const hasAnyFilter = Object.values(values).some(
      (value) => value !== null && value !== '' && value !== undefined
    );

    return hasAnyFilter;
  }
}
